import React, { useMemo, useState } from 'react';
import { AopLink, Card, InternalCard, Pill } from '../ui';
import { auditSummary } from '../copData';
import { exportCsv, stamp } from '../copExport';
import { useCop } from '../CopApp';

const ENTITY_TONE = {
	campaign: 'pink',
	segment: 'cyan',
	experiment: 'purple',
	approval_request: 'amber',
	recommendation: 'blue',
	readiness_check: 'green',
	report: 'dim',
};

const ACTOR_LABEL = { ai_agent: 'AI Agent', user: 'User', system: 'System', integration: 'Integration' };

function avatarText(ev) {
	if (ev.actor_type === 'user') {
		return ev.actor
			.split(/[.\s]/)
			.map((p) => p[0])
			.slice(0, 2)
			.join('')
			.toUpperCase();
	}
	if (ev.actor_type === 'system') return '⚙';
	if (ev.actor_type === 'integration') return '⇄';
	return '⬡';
}

export default function AuditLogView() {
	const { auditEvents, showToast, isInternal, reportDateRangeLabel } = useCop();
	const [search, setSearch] = useState('');
	const [actor, setActor] = useState('all');
	const [entity, setEntity] = useState('all');

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		const visibleEvents = isInternal
			? auditEvents
			: auditEvents.filter(
				(ev) =>
					ev.actor_type === 'user' &&
					['approval_request', 'report'].includes(ev.entity_type)
			);
		return visibleEvents.filter((ev) => {
			if (isInternal && actor !== 'all' && ev.actor_type !== actor) return false;
			if (entity !== 'all' && ev.entity_type !== entity) return false;
			if (q && !`${ev.actor} ${ev.action} ${ev.entity}`.toLowerCase().includes(q)) return false;
			return true;
		});
	}, [auditEvents, search, actor, entity, isInternal]);

	const days = useMemo(() => {
		const groups = [];
		let current = null;
		filtered.forEach((ev) => {
			if (!current || current.day !== ev.day) {
				current = { day: ev.day, events: [] };
				groups.push(current);
			}
			current.events.push(ev);
		});
		return groups;
	}, [filtered]);

	const exportAudit = () => {
		if (!filtered.length) {
			showToast('Nothing to export at the current filters');
			return;
		}
		const rows = filtered.map((ev) => ({
			day: ev.day,
			time: ev.time,
			actor_type: ev.actor_type,
			actor: ev.actor,
			action: ev.action,
			entity: ev.entity,
			entity_type: ev.entity_type,
			source: ev.source,
			reason: ev.reason || (ev.diff ? `${ev.diff.field}: ${ev.diff.before} → ${ev.diff.after}` : ''),
		}));
		exportCsv(`cop-audit-log-${stamp()}.csv`, rows, [
			{ key: 'day', label: 'Day' },
			{ key: 'time', label: 'Time' },
			{ key: 'actor_type', label: 'Actor Type' },
			{ key: 'actor', label: 'Actor' },
			{ key: 'action', label: 'Action' },
			{ key: 'entity', label: 'Entity' },
			{ key: 'entity_type', label: 'Entity Type' },
			{ key: 'source', label: 'Source' },
			{ key: 'reason', label: 'Reason' },
		]);
		showToast(`Exported ${rows.length} audit event${rows.length === 1 ? '' : 's'} (CSV)`);
	};

	const todayCount = filtered.filter((e) => e.day === 'today').length;
	const aiCount = auditSummary.by_actor[0].count;
	const totalActors = auditSummary.by_actor.reduce((s, a) => s + a.count, 0);
	const rejected = filtered.filter((e) => e.action.startsWith('rejected')).length;

	return (
		<main className="cop-page">
			<div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
				<div style={{ flex: 1, minWidth: 260 }}>
					<h1 className="cop-page-title">
						{isInternal ? 'Audit Log' : 'Decision History'} <span className="cop-immutable">🔒 Immutable record</span>
					</h1>
					<div className="cop-page-sub">
						{isInternal
							? `Every AI, system, integration, and human action, recorded. ${reportDateRangeLabel}.`
							: `Client-facing decisions and report activity, recorded. ${reportDateRangeLabel}.`}
					</div>
				</div>
				{isInternal && (
					<button className="cop-btn" onClick={exportAudit}>
						⤓ Export
					</button>
				)}
			</div>

			<div className="cop-audit-filters">
				<input
					className="cop-input"
					style={{ minWidth: 240, flex: 1, maxWidth: 360 }}
					placeholder="Search actions, actors, entities…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				{isInternal && (
					<select className="cop-select" value={actor} onChange={(e) => setActor(e.target.value)}>
						<option value="all">Actor: All</option>
						<option value="ai_agent">AI Agent</option>
						<option value="user">User</option>
						<option value="system">System</option>
						<option value="integration">Integration</option>
					</select>
				)}
				<select className="cop-select" value={entity} onChange={(e) => setEntity(e.target.value)}>
					<option value="all">Entity: All</option>
					{Object.keys(ENTITY_TONE).map((k) => (
						<option key={k} value={k}>{k.replace(/_/g, ' ')}</option>
					))}
				</select>
			</div>

			<div className="cop-audit-layout" style={isInternal ? undefined : { gridTemplateColumns: '1fr' }}>
				<div>
					{days.length === 0 && (
						<Card>
							<div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 18 }}>No events match the current filters.</div>
						</Card>
					)}
					{days.map((g) => (
						<div key={g.day}>
							<div className="cop-day-divider">
								<span>{g.day}</span>
							</div>
							<div className="cop-audit-stream">
								{g.events.map((ev) => (
									<div className="cop-audit-row" key={ev.id}>
										<div className={`cop-actor-avatar ${ev.actor_type}`}>{avatarText(ev)}</div>
										<div className="cop-audit-body">
											<div className="cop-audit-sentence">
												<b>{ev.actor}</b> {ev.action} <span className="ent">{ev.entity}</span>
											</div>
											<div className="cop-audit-meta">
												<Pill tone={ENTITY_TONE[ev.entity_type] || 'dim'}>{ev.entity_type.replace(/_/g, ' ')}</Pill>
												{isInternal && <Pill tone="dim">{ev.source}</Pill>}
												{isInternal && ev.diff && (
													<span className="cop-diff">
														<span className="before">{ev.diff.field} {ev.diff.before}</span>
														<span style={{ color: 'var(--text-dim)' }}>→</span>
														<span className="after">{ev.diff.after}</span>
													</span>
												)}
												{ev.reason && <span className="cop-audit-reason">{ev.reason}</span>}
											</div>
										</div>
										<div className="cop-audit-when">
											<div className="rel">{ev.when}</div>
											<div className="abs">{ev.time}</div>
										</div>
									</div>
								))}
							</div>
						</div>
					))}
				</div>

				{isInternal && (
				<InternalCard title="Activity Summary" icon="∑" className="raise">
					<div style={{ fontFamily: 'var(--font-mono)', fontSize: 30, fontWeight: 600 }}>{todayCount + 41}</div>
					<div style={{ fontSize: 11.5, color: 'var(--text-sec)', marginBottom: 8 }}>Actions today</div>

					<div style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>AI vs Human split</div>
					<div className="cop-split-bar">
						{auditSummary.by_actor.map((a) => (
							<span key={a.key} style={{ width: `${(a.count / totalActors) * 100}%`, background: a.color }} />
						))}
					</div>
					<div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, fontFamily: 'var(--font-mono)', marginBottom: 12 }}>
						<span style={{ color: 'var(--pink)' }}>AI {Math.round((aiCount / totalActors) * 100)}%</span>
						<span style={{ color: 'var(--green)' }}>Human {Math.round((auditSummary.by_actor[1].count / totalActors) * 100)}%</span>
					</div>

					<div className="cop-summary-row">
						<span>Rollbacks</span>
						<span className="v tone-amber">{auditSummary.rollbacks}</span>
					</div>
					<div className="cop-summary-row">
						<span>Rejected actions</span>
						<span className="v tone-red">{rejected + 4}</span>
					</div>
					<div className="cop-summary-row">
						<span>Avg. response time</span>
						<span className="v">{auditSummary.avg_response}</span>
					</div>

					<div className="cop-nav-label" style={{ padding: '14px 0 6px' }}>By actor type</div>
					{auditSummary.by_actor.map((a) => (
						<div className="cop-summary-row" key={a.key}>
							<span>
								<span className="cop-legend-dot" style={{ color: a.color, background: a.color }} />
								{ACTOR_LABEL[a.key]}
							</span>
							<span className="v">{a.count}</span>
						</div>
					))}
					<div className="cop-aop-links">
						<AopLink view="audit-log">AOP audit</AopLink>
						<AopLink view="workflow-runs">AOP workflow runs</AopLink>
					</div>
				</InternalCard>
				)}
			</div>
		</main>
	);
}
